import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useApi } from "@/api/hooks/useApi";
import type { MassAddressCreateRequest, PortfolioExportFilter, UsersMultipleCreateRequest } from "@/api/types";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { downloadFile } from "@/helpers/file-parse";
import { QUERY_KEYS } from "@/query-keys";
import { useTranslation } from "@/translations";

const useExportProjectAnalytics = () => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (payload: { data: PortfolioExportFilter; fileName: string }) => api.postProjectExportV1(payload.data),
    onSuccess: async (data, payload) => {
      const blob = await data.blob();
      downloadFile(blob, payload.fileName);

      showFlashToast({
        variant: "success",
        message: t("page.portfolio.overview.notification.export.success", {
          count: payload.data.projectIds.length,
        }),
      });
    },
    onError: () => {
      showFlashToast({
        variant: "alert",
        message: t("page.portfolio.overview.notification.export.error"),
      });
    },
  });
};

const useAddAdminUsers = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (data: UsersMultipleCreateRequest) => api.postUsersMultipleV1(data),
    onSuccess: (_, payload) => {
      for (const currProjectId of payload.projectIds) {
        void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.USERS_QUERY(currProjectId) });
      }

      showFlashToast({ variant: "success", message: t("page.user-detail.invite-user.success") });
    },
    onError: () => {
      showFlashToast({ variant: "alert", message: t("page.user-detail.invite-user.error") });
    },
  });
};

const useAddMassAddresses = () => {
  const api = useApi();

  return useMutation({
    mutationFn: ({ payload, signal }: { payload: MassAddressCreateRequest; signal?: AbortSignal }) =>
      api.postMassAddressesV1(payload, { format: undefined, signal }),
  });
};

export const portfolioMutations = {
  useExportProjectAnalytics,
  useAddAdminUsers,
  useAddMassAddresses,
};
